// Relances contrats (paiements, renouvellements) — liste de cards

import React from 'react';
import { Card, Tag, Button, Spin, Space, Typography } from 'antd';
import { EyeOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAllBusinesses } from '../../../hooks/useSuperAdmin';
import { useAllContractsWithPayments } from '../../../hooks/useBusinessContracts';
import { isContractExpiringSoon, hasPaymentDelay, hasPendingOtherPayments } from '../../../types/businessContract';
import type { Business } from '../../../types/business';
import type { BusinessContract, BusinessContractPayment } from '../../../types/businessContract';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Text } = Typography;

interface RelanceItem {
  businessId: string;
  businessName: string;
  coverageExpiry: Date | null;
  coverageExpired: boolean;
  contractExpiringSoon: boolean;
  pendingOtherPayments: boolean;
  contractEndDate: Date;
}

const RelancesContratsCard: React.FC = () => {
  const navigate = useNavigate();
  const { data: businesses = [] } = useAllBusinesses();
  const { data: contractData, isLoading } = useAllContractsWithPayments();

  const businessMap = new Map<string, Business>(businesses.map((b: Business) => [b.id, b]));

  const relances: RelanceItem[] =
    contractData
      ?.map(
        ({
          contract,
          payments,
          coverageExpiry,
        }: {
          contract: BusinessContract;
          payments: BusinessContractPayment[];
          coverageExpiry: Date | null;
        }) => {
          const business = businessMap.get(contract.businessId);
          const coverageExpired = hasPaymentDelay(payments);
          const contractExpiringSoon = isContractExpiringSoon(contract, 60);
          const pendingOtherPayments = hasPendingOtherPayments(payments);
          const needsAttention = coverageExpired || contractExpiringSoon || pendingOtherPayments;
          if (!needsAttention || !business) return null;
          return {
            businessId: contract.businessId,
            businessName: business.name,
            coverageExpiry,
            coverageExpired,
            contractExpiringSoon,
            pendingOtherPayments,
            contractEndDate: contract.endDate,
          };
        }
      )
      .filter((x): x is RelanceItem => x != null) ?? [];

  if (isLoading) {
    return (
      <Card title="Relances contrats">
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (relances.length === 0) {
    return (
      <Card title="Relances contrats">
        <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>
          Aucune relance à effectuer pour le moment.
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Relances contrats"
      extra={<Tag color="red">{relances.length} à traiter</Tag>}
      style={{ marginBottom: 24 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {relances.map((r) => (
          <Card
            key={r.businessId}
            size="small"
            styles={{ body: { padding: 12 } }}
            style={{ borderLeft: '3px solid #ff4d4f' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}
            >
              <Space direction="vertical" size={6} style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ fontSize: 15 }}>
                  {r.businessName}
                </Text>
                <Space wrap size={[4, 4]}>
                  {r.coverageExpired && <Tag color="red">Retard de paiement</Tag>}
                  {r.pendingOtherPayments && <Tag color="orange">Frais en attente</Tag>}
                  {r.contractExpiringSoon && <Tag color="orange">Contrat à renouveler</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Couverture : {r.coverageExpiry ? dayjs(r.coverageExpiry).format('MM/YYYY') : '—'}
                  {' · '}
                  Fin contrat : {dayjs(r.contractEndDate).format('DD/MM/YYYY')}
                </Text>
              </Space>
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/super-admin/businesses/${r.businessId}?tab=contract`)}
              >
                Voir
              </Button>
            </div>
          </Card>
        ))}
      </Space>
    </Card>
  );
};

export default RelancesContratsCard;
